import { Table, Typography, Button, Tooltip, Input } from "antd";
import dayjs from "dayjs";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { errorMessage, successMessage } from "../../utils/notificationUtils";
import {
  getUploadedRegisterData,
  updateApproveRegisterData,
} from "../../apis/patientService";
import { ROUTES } from "../../utils/constants";
import { DoubleLeftOutlined, EditOutlined } from "@ant-design/icons";
import ConfirmSubmissionModalStage from "../modals/ConfirmSubmissionModalStage";
import { Pagination } from "antd";
import { PaginationProps } from "antd/lib";

const { Title } = Typography;

const CreditRegisterFileData = () => {
  const { id, type } = useParams();
  const [loading, setLoading] = useState(false);
  const [fileData, setFileData] = useState<any[]>([]);
  const navigate = useNavigate();
  const [pageSize, setPageSize] = useState(50);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalData, setTotalData] = useState<any>(null);

  const handleGetAllData = async (filters: any) => {
    try {
      setLoading(true);
      const res = await getUploadedRegisterData(id, type, filters);
      setFileData(res?.data?.content);
      setTotalData(res?.data?.totalElements);
    } catch (err) {
      errorMessage("Please, Try again later");
      setFileData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const filters = {
      pageSize: pageSize,
      pageNumber: currentPage - 1,
    };
    if (id && type) {
      handleGetAllData(filters);
    }
  }, [id, type]);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [creditNumber, setCreditNumber] = useState("");
  const [confirmationModal, setConfirmationModal] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<any>(null);
  const [isUpdatingCredit, setIsUpdatingCredit] = useState(false);

  const handleEditClick = (record: any) => {
    setEditingId(record.id);
    setCreditNumber(record?.creditNumber || "");
  };

  const handleConfirmClick = (record: any) => {
    setSelectedRecord(record);
    setConfirmationModal(true);
  };

  const handleSave = async () => {
    try {
      setIsUpdatingCredit(true);

      const payload = {
        creditNumber: creditNumber,
      };

      await updateApproveRegisterData(selectedRecord?.id, payload);

      successMessage("Credit Number updated successfully");

      setConfirmationModal(false);
      setEditingId(null);
      const filters = {
        pageSize: pageSize,
        pageNumber: currentPage - 1,
      };
      handleGetAllData(filters); // refresh table
    } catch (err: any) {
      errorMessage(err?.message || "Please, Try again later");
    } finally {
      setIsUpdatingCredit(false);
    }
  };

  const withTooltip = (value: any) => {
    if (value === null || value === undefined) return "-";
    const text = String(value);

    return text.length > 10 ? (
      <Tooltip title={text}>
        <span>{text.slice(0, 10)}...</span>
      </Tooltip>
    ) : (
      <span>{text}</span>
    );
  };

  const formatCurrency = (value: any) => {
    if (value === null || value === undefined) return null;
    const formatted = `₹${Number(value).toLocaleString()}`;
    return withTooltip(formatted);
  };

  const columns = [
    {
      title: "S.NO.",
      key: "sno",
      align: "center" as const,
      width: 80,
        fixed: "left" as const,
      render: (_: any, __: any, index: number) =>
        (currentPage - 1) * pageSize + index + 1,
    },
    {
      title: "PATIENT NAME",
      dataIndex: "patientName",
      key: "patientName",
      width: 170,
      render: withTooltip,
    },
    {
      title: "INSURANCE COMPANY",
      dataIndex: "insuranceCompany",
      key: "insuranceCompany",
      width: 180,
      render: withTooltip,
    },
    {
      title: "THIRD PARTY",
      dataIndex: "thirdParty",
      key: "thirdParty",
      width: 150,
      render: withTooltip,
    },
    {
      title: "HOSPITAL NO",
      dataIndex: "hospitalNo",
      key: "hospitalNo",
      width: 130,
      render: withTooltip,
    },
    {
      title: "DOA",
      dataIndex: "doa",
      key: "doa",
      width: 120,
      align: "center" as const,
      render: (value: any) => (value ? withTooltip(value) : null),
    },
    {
      title: "DOD",
      dataIndex: "dod",
      key: "dod",
      width: 120,
      align: "center" as const,
      render: (value: any) => (value ? withTooltip(value) : null),
    },
    {
      title: "BILL NO",
      dataIndex: "billNumber",
      key: "billNumber",
      width: 140,
      render: withTooltip,
    },
    {
      title: "CLAIM ID",
      dataIndex: "claimId",
      key: "claimId",
      width: 150,
      render: withTooltip,
    },
    {
      title: "BILL AMOUNT",
      dataIndex: "billAmount",
      key: "billAmount",
      width: 130,
      align: "center" as const,
      render: formatCurrency,
    },
    {
      title: "AUTHORIZED AMOUNT",
      dataIndex: "authorizedAmount",
      key: "authorizedAmount",
      width: 190,
      align: "center" as const,
      render: formatCurrency,
    },
    {
      title: "COPAY",
      dataIndex: "copay",
      key: "copay",
      width: 100,
      align: "center" as const,
      render: withTooltip,
    },
    {
      title: "NME",
      dataIndex: "nme",
      key: "nme",
      width: 100,
      align: "center" as const,
      render: withTooltip,
    },
    {
      title: "TDS",
      dataIndex: "tds",
      key: "tds",
      width: 120,
      align: "center" as const,
      render: formatCurrency,
    },
    {
      title: "SETTLED AMOUNT",
      dataIndex: "settledAmount",
      key: "settledAmount",
      width: 150,
      align: "center" as const,
      render: formatCurrency,
    },
    {
      title: "UTR NO",
      dataIndex: "utrNo",
      key: "utrNo",
      width: 150,
      render: withTooltip,
    },
    {
      title: "SETTLEMENT DATE",
      dataIndex: "settlementDate",
      key: "settlementDate",
      width: 150,
      align: "center" as const,
      render: (value: any) => (value ? withTooltip(value) : null),
    },
    // {
    //   title: "CREDIT NUMBER",
    //   dataIndex: "creditNumber",
    //   key: "creditNumber",
    //   width: 170,
    //   fixed: "right" as const,
    //   render: withTooltip,
    // },
    {
      title: "CREDIT NUMBER",
      dataIndex: "creditNumber",
      key: "creditNumber",
      width: 220,
      fixed: "right" as const,
      render: (value: any, record: any) => {
        const isEditing = editingId === record.id;

        return isEditing ? (
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <Input
              value={creditNumber}
              onChange={(e) => setCreditNumber(e.target.value)}
              size="small"
              style={{ width: 120 }}
            />

            <Button
              type="primary"
              size="small"
              onClick={() => handleConfirmClick(record)}
            >
              ✔
            </Button>

            <Button size="small" onClick={() => setEditingId(null)}>
              ✖
            </Button>
          </div>
        ) : (
          <div style={{ display: "flex", alignItems: "center", gap: 6}}>
            {withTooltip(value)}
            <EditOutlined
              style={{ color: "#004C8F", cursor: "pointer" }}
              onClick={() => handleEditClick(record)}
            />
          </div>
        );
      },
    },

    {
      title: "UPDATED AT",
      dataIndex: "updatedAt",
      key: "updatedAt",
      width: 170,
      align: "center" as const,
      render: (value: any) =>
        value ? withTooltip(dayjs(value).format("DD/MM/YYYY HH:mm:ss")) : null,
    },
    {
      title: "Action",
      key: "action",
      fixed: "right" as const,
      render: (record: any) => (
        <Button
          type="primary"
          style={{
            background: "rgb(0, 76, 143)",
            width: "100px",
            fontSize: "0.84rem",
            color: "white",
          }}
          disabled={record.isApproved || !record?.creditNumber}
          onClick={() => {
            const payload = {
              isApproved: true,
            };
            handleUpdateApproveRegisterData(record?.id, payload);
          }}
        >
          Approve
        </Button>
      ),
    },
  ];

  const columnCredit = [
    {
      title: "S.NO.",
      key: "sno",
      align: "center" as const,
      width: 80,
      render: (_: any, __: any, index: number) =>
        (currentPage - 1) * pageSize + index + 1,
    },
    {
      title: "PATIENT NAME",
      dataIndex: "patientName",
      key: "patientName",
      width: 180,
      render: withTooltip,
    },
    {
      title: "IP NO",
      dataIndex: "ipNo",
      key: "ipNo",
      width: 150,
      render: withTooltip,
    },
    {
      title: "BILL NO",
      dataIndex: "billNo",
      key: "billNo",
      width: 160,
      render: withTooltip,
    },
    {
      title: "BILL DATE",
      dataIndex: "billDate",
      key: "billDate",
      width: 140,
      align: "center" as const,
      render: (value: any) =>
        value ? withTooltip(dayjs(value).format("DD/MM/YYYY")) : null,
    },
    {
      title: "CLAIM NO",
      dataIndex: "claimNo",
      key: "claimNo",
      width: 160,
      render: withTooltip,
    },
    {
      title: "SCHEME NAME",
      dataIndex: "schemeName",
      key: "schemeName",
      width: 170,
      render: withTooltip,
    },
    {
      title: "DISCOUNT AMOUNT",
      dataIndex: "discountAmount",
      key: "discountAmount",
      width: 160,
      align: "center" as const,
      render: formatCurrency,
    },
    {
      title: "TOTAL BILL AMOUNT",
      dataIndex: "totalBillAmount",
      key: "totalBillAmount",
      width: 170,
      align: "center" as const,
      render: formatCurrency,
    },
    {
      title: "SETTLEMENT AMOUNT",
      dataIndex: "settlementAmount",
      key: "settlementAmount",
      width: 170,
      align: "center" as const,
      render: formatCurrency,
    },
    {
      title: "CREDIT NUMBER",
      dataIndex: "creditNumber",
      key: "creditNumber",
      width: 220,
      fixed: "right" as const,
      render: (value: any, record: any) => {
        const isEditing = editingId === record.id;

        return isEditing ? (
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <Input
              value={creditNumber}
              onChange={(e) => setCreditNumber(e.target.value)}
              size="small"
              style={{ width: 120 }}
            />

            <Button
              type="primary"
              size="small"
              onClick={() => handleConfirmClick(record)}
            >
              ✔
            </Button>

            <Button size="small" onClick={() => setEditingId(null)}>
              ✖
            </Button>
          </div>
        ) : (
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            {withTooltip(value)}
            <EditOutlined
              style={{ color: "#004C8F", cursor: "pointer" }}
              onClick={() => handleEditClick(record)}
            />
          </div>
        );
      },
    },
    // {
    //   title: "CREATED AT",
    //   dataIndex: "createdAt",
    //   key: "createdAt",
    //   width: 180,
    //   align: "center" as const,
    //   render: (value: any) =>
    //     value ? withTooltip(dayjs(value).format("DD/MM/YYYY HH:mm:ss")) : null,
    // },
    {
      title: "UPDATED AT",
      dataIndex: "updatedAt",
      key: "updatedAt",
      width: 180,
      align: "center" as const,
      render: (value: any) =>
        value ? withTooltip(dayjs(value).format("DD/MM/YYYY HH:mm:ss")) : null,
    },
    {
      title: "ACTION",
      key: "action",
      align: "center" as const,
      width: 120,
      fixed: "right" as const,
      render: (record: any) => (
        <Button
          type="primary"
          style={{
            background: "rgb(0, 76, 143)",
            width: "100px",
            fontSize: "0.84rem",
            color: "white",
          }}
          disabled={record.isApproved || !record?.creditNumber}
          onClick={() => {
            const payload = { isApproved: true };
            handleUpdateApproveRegisterData(record?.id, payload);
          }}
        >
          Approve
        </Button>
      ),
    },
  ];

  const handleUpdateApproveRegisterData = async (id: any, payload: any) => {
    try {
      const res = await updateApproveRegisterData(id, payload);
      if (payload?.isApproved) {
        successMessage("Data Approved Successfully");
      } else {
        successMessage("Credit Number updated successfully");
      }
      const filters = {
        pageSize: pageSize,
        pageNumber: currentPage - 1,
      };
      handleGetAllData(filters);
    } catch (err: any) {
      errorMessage(err?.message || "Please, Try again later");
    }
  };

  const onPageChange: PaginationProps["onChange"] = async (
    page,
    pageSizeCurr,
  ) => {
    if (pageSizeCurr == pageSize) {
      setCurrentPage(page);

      const updatedFilters = {
        pageSize: pageSize,
        pageNumber: page - 1,
      };

      await handleGetAllData(updatedFilters);
    }
  };

  const onShowSizeChange = async (current: number, newPageSize: number) => {
    setPageSize(newPageSize);

    const updatedFilters = {
      pageSize: newPageSize,
      pageNumber: 0,
    };

    await handleGetAllData(updatedFilters);
  };

  return (
    <div style={{ padding: "20px" }}>
      <Button
        style={{
          border: "1px solid #004C8F",
          color: "#004C8F",
          fontWeight: 600,
          borderRadius: 20,
          marginBottom: 16,
        }}
        onClick={() => navigate(ROUTES.CREDIT_REGISTER_UPLOAD)}
      >
        <DoubleLeftOutlined /> REGISTER LIST
      </Button>

      <div
        style={{
          background: "#ffffff",
          padding: "20px",
          borderRadius: "12px",
          boxShadow: "0 4px 12px rgba(0, 76, 143, 0.08)",
          border: "1px solid rgba(0, 76, 143, 0.2)",
          // overflow: "hidden",
        }}
      >
        <Title
          level={4}
          style={{
            marginBottom: 20,
            fontWeight: 700,
            color: "#004C8F",
          }}
        >
          Register File Details
        </Title>

        <Table
          className="register-table"
          dataSource={fileData}
          columns={type === "credit" ? columnCredit : columns}
          rowKey="id"
          bordered
          loading={loading}
          sticky
          size="middle"
          pagination={false}
          scroll={{ x: "max-content", y: 600 }}
        />
      </div>

      <div style={{ width: "100%", display: "flex", justifyContent: "end" }}>
        <Pagination
          size="small"
          onChange={onPageChange}
          showSizeChanger
          style={{ marginTop: "16px" }}
          current={currentPage}
          total={totalData}
          onShowSizeChange={onShowSizeChange}
          pageSizeOptions={["10", "20", "50", "100"]}
          defaultPageSize={50}
          pageSize={pageSize}
        />
      </div>

      <ConfirmSubmissionModalStage
        open={confirmationModal}
        title="Are you sure you want to update credit number ??"
        handleCancel={() => setConfirmationModal(false)}
        handleSave={handleSave}
        isLoading={isUpdatingCredit}
      />
    </div>
  );
};

export default CreditRegisterFileData;
